import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { DelightToast } from '@/components/delight/DelightToast';
import { MicroConfetti } from '@/components/delight/MicroConfetti';
import { useDelightPreferences } from './useDelightPreferences';
import type {
  CelebrationType,
  DelightEvent,
  DelightTriggerOptions,
  DelightPreferences,
} from '@/lib/delight/types';
import { CELEBRATION_CONFIGS } from '@/lib/delight/types';
import { shouldShowConfetti } from '@/lib/delight/milestones';
import { createElement } from 'react';
import { createRoot } from 'react-dom/client';

/**
 * Check if a specific celebration type is enabled in preferences
 */
function getCelebrationEnabled(type: CelebrationType, preferences: DelightPreferences): boolean {
  const mapping: Record<CelebrationType, keyof DelightPreferences> = {
    weekly_update: 'celebrateWeeklyUpdates',
    health_boost: 'celebrateHealthBoosts',
    sync_champion: 'celebrateSyncMilestones',
    growth: 'celebrateGrowth',
    bid_submitted: 'celebrateBidSubmissions',
    bid_won: 'celebrateBidSubmissions', // Wins use same preference as submissions
  };

  const prefKey = mapping[type];
  return preferences[prefKey] as boolean;
}

/**
 * useDelight - Hook for triggering delight celebrations
 *
 * Automatically checks user preferences and fires appropriate celebrations.
 * Supports confetti for special milestones.
 */
export function useDelight() {
  const { preferences, isLoading } = useDelightPreferences();
  const [showConfetti, setShowConfetti] = useState(false);

  const celebrate = useCallback(
    (event: DelightEvent, options?: DelightTriggerOptions) => {
      // Skip if preferences are loading (unless forced)
      if (isLoading && !options?.skipPreferenceCheck) {
        return;
      }

      // Check if celebrations are enabled globally
      if (!options?.skipPreferenceCheck && !preferences.enableCelebrations) {
        return;
      }

      // Check if this specific celebration type is enabled
      const celebrationEnabled = getCelebrationEnabled(event.type, preferences);
      if (!options?.skipPreferenceCheck && !celebrationEnabled) {
        return;
      }

      const config = CELEBRATION_CONFIGS[event.type];

      // Show toast with custom content
      toast.custom(
        () =>
          createElement(DelightToast, {
            type: event.type,
            message: event.message,
            subtitle: event.subtitle,
          }),
        {
          duration: options?.duration || 5000,
          position: 'top-center',
        },
      );

      // Determine if confetti should show
      const milestoneValue = (event.metadata?.milestone as number) || 0;
      const shouldConfetti =
        preferences.enableConfetti &&
        (options?.forceConfetti || config.showConfetti || shouldShowConfetti(milestoneValue));

      if (shouldConfetti) {
        // Create a container for confetti
        const container = document.createElement('div');
        container.id = 'confetti-container';
        document.body.appendChild(container);

        const root = createRoot(container);
        root.render(
          createElement(MicroConfetti, {
            trigger: true,
            duration: 2500,
            onComplete: () => {
              root.unmount();
              container.remove();
            },
          }),
        );
      }
    },
    [preferences, isLoading],
  );

  // Convenience methods for each celebration type
  const celebrateWeeklyUpdate = useCallback(
    (recipientCount?: number) => {
      celebrate({
        type: 'weekly_update',
        message: recipientCount
          ? `Sent to ${recipientCount} recipient${recipientCount > 1 ? 's' : ''}!`
          : 'Your weekly update has been sent!',
        subtitle: 'Keeping clients in the loop',
      });
    },
    [celebrate],
  );

  const celebrateHealthBoost = useCallback(
    (entityType: string) => {
      celebrate({
        type: 'health_boost',
        message: `${entityType} health improved to green!`,
        subtitle: 'Great work keeping things on track',
      });
    },
    [celebrate],
  );

  const celebrateSyncMilestone = useCallback(
    (count: number, milestone: number) => {
      const isFirst = milestone === 1;
      const isCentury = milestone === 100;
      const isBig = milestone >= 100;

      celebrate(
        {
          type: 'sync_champion',
          message: isFirst
            ? 'Your first task synced to ClickUp!'
            : `${count} tasks synced to ClickUp!`,
          subtitle: isFirst
            ? "You're on your way!"
            : isCentury
              ? "Century! You're a sync champion!"
              : isBig
                ? 'Incredible dedication!'
                : 'Keep up the great work!',
          metadata: { milestone, count },
        },
        { forceConfetti: milestone >= 100 },
      );
    },
    [celebrate],
  );

  const celebrateGrowth = useCallback(
    (entityType: 'client' | 'contract' | 'goal' | 'project') => {
      const messages: Record<string, { message: string; subtitle: string }> = {
        client: {
          message: 'New client added to your portfolio!',
          subtitle: 'Growing your business',
        },
        contract: {
          message: 'New contract added!',
          subtitle: 'Revenue opportunity tracked',
        },
        goal: {
          message: 'New goal set!',
          subtitle: "Let's achieve this together",
        },
        project: {
          message: 'Project connected!',
          subtitle: 'Now synced with ClickUp',
        },
      };

      const { message, subtitle } = messages[entityType];
      celebrate({
        type: 'growth',
        message,
        subtitle,
      });
    },
    [celebrate],
  );

  /**
   * Celebrate a job bid/application submission
   */
  const celebrateBidSubmitted = useCallback(
    (todayCount: number, dailyTarget: number) => {
      const isTargetMet = todayCount >= dailyTarget;
      const isStreak = todayCount > 1;
      const isMilestone = todayCount === 5 || todayCount === 10 || todayCount % 25 === 0;

      let message = `Application #${todayCount} submitted!`;
      let subtitle = `${dailyTarget - todayCount} more to hit today's target`;

      if (isTargetMet) {
        message = `Daily target reached! (${todayCount}/${dailyTarget})`;
        subtitle = "You're crushing it today!";
      } else if (isMilestone) {
        message = `Milestone: ${todayCount} applications submitted!`;
        subtitle = 'Keep the momentum going!';
      } else if (isStreak && todayCount > 3) {
        message = `${todayCount} and counting!`;
        subtitle = "You're on fire!";
      }

      celebrate(
        {
          type: 'bid_submitted',
          message,
          subtitle,
          metadata: { todayCount, dailyTarget, isTargetMet, isMilestone },
        },
        { forceConfetti: isTargetMet || isMilestone },
      );
    },
    [celebrate],
  );

  /**
   * Celebrate a job bid win
   */
  const celebrateBidWon = useCallback(
    (jobTitle?: string, winCount?: number) => {
      const isFirst = winCount === 1;
      const isMilestone = winCount && (winCount === 5 || winCount === 10 || winCount % 25 === 0);

      let message = jobTitle ? `Won: ${jobTitle.slice(0, 40)}...` : 'New client acquired!';
      let subtitle = 'Your hard work paid off!';

      if (isFirst) {
        message = 'First win!';
        subtitle = "You're on your way to success!";
      } else if (isMilestone) {
        message = `${winCount} wins and counting!`;
        subtitle = "You're a closing machine!";
      }

      celebrate(
        {
          type: 'bid_won',
          message,
          subtitle,
          metadata: { jobTitle, winCount, isFirst, isMilestone },
        },
        { forceConfetti: true, duration: 8000 }, // Longer duration for wins
      );
    },
    [celebrate],
  );

  return {
    celebrate,
    celebrateWeeklyUpdate,
    celebrateHealthBoost,
    celebrateSyncMilestone,
    celebrateGrowth,
    celebrateBidSubmitted,
    celebrateBidWon,
    preferences,
    isLoading,
    showConfetti,
  };
}
